import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { StoreService } from '../../store.service';
import {
  ModalDismissReasons,
  NgbDate,
  NgbModal,
} from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { DealerService } from '../../dealer.service';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AiSectionConfig, parseAiInsightSections } from 'src/app/core/ai-summary-parser';

export type OrderLifecycleStatus =
  | 'Pending Payment'
  | 'Paid'
  | 'Processing'
  | 'In Transit'
  | 'Shipped'
  | 'Delivered'
  | 'Completed'
  | 'Cancelled'
  | string;

export interface CustomerOrderHistory {
  order_id: string;
  order_date: string;
  total_amount: number;
  currency: string;
  items_count: number;
  payment_type: string;
  status: OrderLifecycleStatus;
  items_preview?: string;
  is_paid?: boolean;
}

export interface CustomerJourneyEvent {
  id: string;
  raw_timestamp?: number;
  event_name: string;
  display_name?: string;
  event_category: 'commerce' | 'session' | 'screen' | 'api' | 'interaction' | 'milestone' | 'marketing' | 'delivery' | 'communication' | 'lifecycle' | 'order' | 'payment';
  category_label?: string;
  category_icon?: string;
  category_color?: string;
  timestamp: string;
  date_group: string;
  badge_count?: number;
  status_badge?: string;
  action_type?: 'replay' | 'whatsapp' | 'order' | 'copy';
  action_label?: string;
  action_value?: string;
  expanded?: boolean;
  properties: { [key: string]: any };
}

export interface CustomerProfileProperty {
  key: string;
  value: any;
  icon?: string;
  systemProperty?: boolean;
}

export interface EnhancedCustomer {
  customer_id: string | number;
  customer_name: string;
  customer_email: string;
  customer_mobile_prefix: string;
  customer_mobile: string;
  country_code: string;
  city?: string;
  state?: string;
  postal_code?: string;
  address_line?: string;
  avatar_initials?: string;
  avatar_gradient?: string;
  tier: 'VIP' | 'Repeat' | 'One-time buyer' | 'Not yet paid' | string;
  lifecycle_badge?: 'VIP' | 'Repeat' | 'One-time buyer' | 'Not yet paid' | string;
  is_new?: boolean;
  status: 'Active' | 'Occasional' | 'Inactive';
  total_orders: number;
  lifetime_paid_orders?: number;
  pending_orders?: number;
  unpaid_orders?: number;
  cancelled_orders?: number;
  order_breakdown?: { count: number; label: string }[];
  total_spent: number;
  pending_spend?: number;
  other_currency_spend?: { currency: string; amount: number }[];
  average_order_value: number;
  currency: string;
  preferred_payment: string;
  first_order_date: string;
  last_order_date: string;
  last_order_relative: string;
  latest_order_status: OrderLifecycleStatus;
  tags: string[];
  notes?: string;
  recent_orders?: CustomerOrderHistory[];
}

export interface AiInsightSection {
  theme: 'overview' | 'performers' | 'friction' | 'recommendations';
  title: string;
  icon: string;
  rows: {
    label?: string;
    value?: string;
    note?: string;
    text?: string;
    url?: string;
    index?: number;
  }[];
}

const CUSTOMER_AI_SECTIONS: AiSectionConfig[] = [
  { theme: 'overview', title: 'Customer Overview', icon: 'fi-rr-chart-pie-alt', phrases: ['customer overview', 'executive overview', 'overview'] },
  { theme: 'performers', title: 'Your Best Customers', icon: 'fi-rr-crown', phrases: ['your best customers', 'best customers', 'top customer segments', 'top customers', 'top spenders'] },
  { theme: 'friction', title: 'Customers You Could Lose', icon: 'fi-rr-exclamation', phrases: ['customers you could lose', 'customers at risk', 'retention', 'at-risk'] },
  { theme: 'recommendations', title: 'What To Do Next', icon: 'fi-rr-bulb', phrases: ['what to do next', 'actionable recommendations', 'recommendations', 'next steps', 'action plan'] },
];

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.scss'],
})
export class CustomersComponent implements OnInit {
  @Input() embedded = false;
  // Customer export is temporarily disabled; set to true to bring the button back.
  showExportButton = false;
  bearer;
  storeId;
  currency = 'MYR';

  /** AI Assistant / Summary state */
  isGeneratingAiSummary = false;
  aiSummaryContent = '';
  aiSummaryError: string | null = null;
  showAiSummaryCard = false;
  isCopied = false;
  enableAIAssistant = false;
  /** Credits one AI summary costs, from the backend (AI_REPORT_SUMMARY_CREDIT); null until loaded or when not charged. */
  aiTokenCost: number | null = null;
  /** Purpose of the clicked AI button (its data-ai-purpose), sent to Mixpanel as ai_purpose. */
  aiPurpose = 'generate summary';
  parsedSections: AiInsightSection[] = [];
  private readonly aiSampleLimit = 200;
  private copyTimeout: any;

  // Navigation View: 'list' (Table/Grid) or 'detail' (Mixpanel user profile page)
  currentView: 'list' | 'detail' = 'list';

  // Customer Journey & Mixpanel Activity Feed
  customerEvents: CustomerJourneyEvent[] = [];
  groupedEvents: { date_group: string; events: CustomerJourneyEvent[] }[] = [];
  eventSearchQuery = '';
  selectedEventCategory = 'ALL';
  propertySearchQuery = '';
  customerPropertiesList: CustomerProfileProperty[] = [];
  filteredPropertiesList: CustomerProfileProperty[] = [];
  technicalPropertiesList: CustomerProfileProperty[] = [];
  filteredTechnicalPropertiesList: CustomerProfileProperty[] = [];
  isTechnicalDetailsOpen = false;
  activeTimelineRange: '30d' | '7d' | 'all' = '30d';
  hideEventsDropdownOpen = false;
  hiddenCategories: Set<string> = new Set();

  customers: EnhancedCustomer[] = [];
  allCustomers: EnhancedCustomer[] = [];
  filteredCustomers: EnhancedCustomer[] = [];
  summary: any = null;
  rawCustomerApiResponse: any = null;

  mobileOrEmail = '';
  email = '';
  page = 1;
  limit = 20;
  selectedPage = 20;
  start = 0;
  end = 0;
  allCustomerLength = 0;
  dataIsLoading = true;

  // View Mode: table or grid
  viewMode: 'table' | 'grid' = 'table';

  // Segment Filter Tabs
  activeSegment: 'ALL' | 'VIP' | 'REPEAT' | 'ONE_TIME' | 'NOT_PAID' | 'AT_RISK' = 'ALL';

  // Sort Options (Default to Recently Active)
  sortField: 'total_spent' | 'total_orders' | 'last_order_date' | 'customer_name' = 'last_order_date';
  sortAsc = false;

  // Selected Customer for Details Modal / Drawer
  selectedCustomer: EnhancedCustomer | null = null;
  // In-flight detail request, cancelled when another customer is opened so a slow response cannot overwrite it
  private customerDetailSub?: Subscription;

  // Date filters
  minDate;
  maxDate;
  startDate;
  endDate;
  currentDate;
  day;
  month;
  year;
  lastDayOfLastMonth;
  myDate = new Date();
  date = {
    type: null,
  };

  country = [];
  customerCountry = [];
  filteredCountry = '';
  closeResult = '';

  DATE_FILTERS = [
    { display: 'Today', value: 'today', range: false, selectable: false },
    { display: 'Yesterday', value: 'yesterday', range: false, selectable: false },
    { display: 'Current Month', value: 'cm', range: false, selectable: false },
    { display: 'Last Month', value: 'lm', range: false, selectable: false },
    { display: 'Last 30 Days', value: 'last30', range: false, selectable: false },
  ];

  exportType = [
    { display: 'Csv', value: 'csv' },
    { display: 'PDF', value: 'pdf' },
  ];

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate;
  toDate: NgbDate | null = null;
  dateFilter: any;

  constructor(
    private dealerService: DealerService,
    private svc: StoreService,
    public utils: UtilitiesService,
    private modalService: NgbModal
  ) {
    this.dateFilter = this.DATE_FILTERS[2];
  }

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    this.day = this.myDate.getDate();
    this.month = this.myDate.getMonth();
    this.year = this.myDate.getFullYear();
    this.lastDayOfLastMonth = new Date(
      this.myDate.getFullYear(),
      this.myDate.getMonth(),
      0
    ).getDate();
    this.currentDate = this.year + '-' + (this.month + 1) + '-' + this.day;
    this.startDate = this.year + '-' + (this.month + 1) + '-01';
    this.endDate = this.currentDate;
    this.minDate = { year: 2021, month: 1, day: 1 };
    this.maxDate = { year: this.year + 2, month: 12, day: 31 };

    this.loadAiCreditState();
    this.fetchCountry();
    this.datePeriod(true);
  }

  fetchCountry() {
    this.svc.getCountry(this.bearer).subscribe((res) => {
      res['data']?.forEach((item) => {
        if (item['country_name'] === 'Malaysia-Brunei') {
          item['country_name'] = 'Brunei';
        }
      });

      this.country = [{ country_code: '', country_name: 'All Countries' }];
      if (res['data']) {
        res['data'].forEach((value) => {
          value.country_name = value.country_name.split('-').pop().trim();
          this.country.push(value);
        });
      }
    });
  }

  download(type: string) {
    const param = {
      store_id: this.storeId,
      date: this.startDate + ',' + this.endDate,
      mobileOrEmail: this.mobileOrEmail,
      email: this.email,
      page: this.page,
      limit: this.limit,
      type: type,
      country_code: this.filteredCountry,
    };
    this.svc.downloadRequest(this.bearer, '/customer/export', param);
  }

  datePeriod(search = false) {
    this.DATE_FILTERS.forEach((item) => {
      item.selectable = false;
    });

    if (this.dateFilter) {
      this.dateFilter.selectable = true;
    }

    let start = moment();
    let end = moment();

    if (this.dateFilter?.value === 'today') {
      start = moment().startOf('day');
      end = moment().endOf('day');
    } else if (this.dateFilter?.value === 'yesterday') {
      const yesterday = moment().subtract(1, 'days');
      start = yesterday.clone().startOf('day');
      end = yesterday.clone().endOf('day');
    } else if (this.dateFilter?.value === 'cm' || this.dateFilter?.value === 'curr_month') {
      start = moment().startOf('month');
      end = moment();
    } else if (this.dateFilter?.value === 'lm' || this.dateFilter?.value === 'last_month') {
      const lastMonth = moment().subtract(1, 'month');
      start = lastMonth.clone().startOf('month');
      end = lastMonth.clone().endOf('month');
    } else if (this.dateFilter?.value === 'last30') {
      end = moment();
      start = moment().subtract(30, 'days');
    } else {
      start = moment().startOf('month');
      end = moment();
    }

    if (start && end) {
      const rangeVal = `${start.format('DD-MMM-YYYY')} to ${end.format('DD-MMM-YYYY')}`;
      this.date.type = {
        value: { start: start, end: end },
        display: this.dateFilter?.display || rangeVal,
        range: true,
      };
      this.startDate = `${start.format('YYYY-MM-DD')}`;
      this.endDate = `${end.format('YYYY-MM-DD')}`;

      // Synchronize NgbDate objects for the custom picker
      this.fromDate = new NgbDate(start.year(), start.month() + 1, start.date());
      this.toDate = new NgbDate(end.year(), end.month() + 1, end.date());
    }
    this.page = 1;
    if (search) this.getCustomer();
  }

  dateUpdated(filter) {
    this.dateFilter = filter;
    this.datePeriod();
    this.getCustomer();
  }

  clearCustomerSearch() {
    this.mobileOrEmail = '';
    this.email = '';
    this.page = 1;
    this.getCustomer();
  }

  searchMobileOrEmail(event) {
    this.mobileOrEmail = (event?.target?.value || '').trim();
    this.page = 1;
    this.getCustomer();
  }

  getCustomer() {
    const t = this.startDate + ',' + this.endDate;
    this.dataIsLoading = true;
    this.customers = [];

    this.svc
      .getCustomerList(
        this.bearer,
        this.storeId,
        this.mobileOrEmail,
        this.email,
        t,
        this.page,
        this.selectedPage,
        this.filteredCountry,
        this.activeSegment,
        this.sortField,
        this.sortAsc ? 'asc' : 'desc'
      )
      .subscribe(
        (res: any) => {
          this.rawCustomerApiResponse = res?.['data'] || null;
          const apiList = res?.['data']?.['data'];
          const pagination = res?.['data']?.['pagination'];
          const summary = res?.['data']?.['summary'];

          if (
            res?.['status'] === 'success' &&
            Array.isArray(apiList)
          ) {
            this.allCustomers = apiList.map((c, i) => this.enrichCustomer(c, i));
            this.summary = summary || null;
            this.allCustomerLength = pagination?.['total_record'] ?? this.allCustomers.length;
            this.page = pagination?.['page'] ?? 1;
            this.start = pagination?.['offset'] ?? (this.page - 1) * this.selectedPage;
            this.end = Math.min(this.start + this.allCustomers.length, this.allCustomerLength);
          } else {
            this.allCustomers = [];
            this.allCustomerLength = 0;
            this.summary = null;
            this.start = 0;
            this.end = 0;
          }

          this.updateCountryList();
          this.applyLocalFilters();
          this.dataIsLoading = false;
        },
        () => {
          this.rawCustomerApiResponse = null;
          this.allCustomers = [];
          this.allCustomerLength = 0;
          this.summary = null;
          this.start = 0;
          this.end = 0;
          this.updateCountryList();
          this.applyLocalFilters();
          this.dataIsLoading = false;
        }
      );
  }

  private updateCountryList() {
    this.customerCountry = [{ country_code: '' }];
    this.allCustomers.forEach((value) => {
      if (
        value.country_code &&
        !this.customerCountry.find((field) => field.country_code === value.country_code)
      ) {
        this.customerCountry.push({ country_code: value.country_code });
      }
    });
  }

  /**
   * Helper to format customer names in clean Title Case
   */
  formatCustomerName(name: string): string {
    if (!name || typeof name !== 'string') return '';
    return name
      .trim()
      .split(/\s+/)
      .map((w) => {
        if (!w) return '';
        return w
          .split('-')
          .map((sub) => sub.charAt(0).toUpperCase() + sub.slice(1).toLowerCase())
          .join('-');
      })
      .join(' ');
  }

  /**
   * Helper to format location cleanly without duplicate state/city
   */
  formatLocation(city?: string, state?: string, countryCode?: string): string {
    const parts: string[] = [];
    const c = (city || '').trim();
    const s = (state || '').trim();
    const cc = (countryCode || 'MY').trim().toUpperCase();

    if (c && !/^\d+$/.test(c)) {
      parts.push(c);
    }
    if (s && !/^\d+$/.test(s) && s.toLowerCase() !== c.toLowerCase()) {
      parts.push(s);
    }
    if (cc && !/^\d+$/.test(cc)) {
      parts.push(cc);
    }
    return parts.join(', ');
  }

  /**
   * Helper to format payment method display (e.g. cod -> COD, non_cod -> Online Payment)
   */
  formatPaymentMethod(type: string): string {
    if (!type || typeof type !== 'string') return 'Online Payment';
    const clean = type.trim().toLowerCase();
    if (clean === 'cod' || clean === 'cash_on_delivery' || clean === 'cash on delivery') {
      return 'COD';
    }
    if (
      clean === 'non_cod' ||
      clean === 'non-cod' ||
      clean === 'non cod' ||
      clean === 'online banking' ||
      clean === 'online_banking' ||
      clean === 'online payment' ||
      clean === 'online_payment' ||
      clean === 'payment_gateway' ||
      clean === 'gateway' ||
      clean === 'credit_card' ||
      clean === 'fpx' ||
      clean === 'senangpay' ||
      clean === 'stripe' ||
      clean === '2c2p'
    ) {
      return 'Online Payment';
    }
    if (clean === 'manual' || clean === 'manual_transfer' || clean === 'bank_transfer') {
      return 'Bank Transfer';
    }
    return clean
      .replace(/_/g, ' ')
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /**
   * Enriches customer object with initials, gradients, relative dates, and clean data mappings
   */
  private enrichCustomer(raw: any, index: number): EnhancedCustomer {
    const id = raw.customer_id || `CUST-${raw.latest_fiv5s_order_id || raw.latest_store_order_id || raw.id || index + 1}`;
    const name = this.formatCustomerName(raw.customer_name || raw.name || '');
    let email = (raw.customer_email || raw.email || '').trim().toLowerCase();
    if (email === '-' || email === 'null' || email === 'undefined') {
      email = '';
    }
    const mobile = raw.customer_mobile || raw.mobile || '';
    const prefix = raw.customer_mobile_prefix || raw.mobile_prefix || '60';
    const countryCode = (raw.country_code || 'my').toLowerCase();

    // Derived initials
    const words = (name || '').trim().split(' ').filter(Boolean);
    const initials =
      words.length >= 2
        ? `${words[0][0]}${words[1][0]}`.toUpperCase()
        : (name ? name.slice(0, 2).toUpperCase() : (mobile ? mobile.slice(-2) : 'CU'));

    const totalOrders = Number(raw.total_orders || 0);
    const totalSpent = Number(raw.total_spent || 0);
    const lifetimePaidOrders = Number(raw.lifetime_paid_orders !== undefined ? raw.lifetime_paid_orders : (raw.paid_orders !== undefined ? raw.paid_orders : (totalOrders > 0 && totalSpent > 0 ? totalOrders : 0)));
    const aov = Number(raw.average_order_value || (lifetimePaidOrders > 0 ? +(totalSpent / lifetimePaidOrders).toFixed(2) : 0));

    // Orders that don't count toward spend: COD/self-collect awaiting collection, unpaid checkouts, cancelled
    const pendingOrders = Number(raw.pending_orders || 0);
    const unpaidOrders = Number(raw.unpaid_orders || 0);
    const cancelledOrders = Number(raw.cancelled_orders || 0);
    const orderBreakdown = [
      { count: pendingOrders, label: 'awaiting collection' },
      { count: unpaidOrders, label: 'unpaid' },
      { count: cancelledOrders, label: 'cancelled' },
    ].filter((part) => part.count > 0);

    const lifecycleBadge = raw.lifecycle_badge || raw.tier || 'Not yet paid';
    const isNew = !!raw.is_new;

    const gradients = [
      'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
      'linear-gradient(135deg, #059669 0%, #047857 100%)',
      'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
      'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
      'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
    ];
    const avatarGradient = raw.avatar_gradient || gradients[index % gradients.length];
    const preferredPayment = this.formatPaymentMethod(raw.preferred_payment || raw.payment_type || 'Online Payment');

    const city = raw.city || '';
    const state = raw.state || '';
    const postalCode = raw.postal_code || '';
    const addressLine = raw.address_line || '';

    const lastOrderDate = raw.last_order_date || '';
    const lastOrderRelative = raw.last_order_relative || (raw.last_order_date ? moment(raw.last_order_date).fromNow() : '—');
    const latestOrderStatus = raw.latest_order_status || 'Completed';

    return {
      customer_id: id,
      customer_name: name,
      customer_email: email,
      customer_mobile_prefix: prefix,
      customer_mobile: mobile,
      country_code: countryCode,
      city: city,
      state: state,
      postal_code: postalCode,
      address_line: addressLine,
      avatar_initials: initials,
      avatar_gradient: avatarGradient,
      tier: lifecycleBadge,
      lifecycle_badge: lifecycleBadge,
      is_new: isNew,
      status: raw.status || (raw.is_at_risk ? 'Inactive' : 'Active'),
      total_orders: totalOrders,
      lifetime_paid_orders: lifetimePaidOrders,
      pending_orders: pendingOrders,
      unpaid_orders: unpaidOrders,
      cancelled_orders: cancelledOrders,
      order_breakdown: orderBreakdown,
      total_spent: totalSpent,
      pending_spend: Number(raw.pending_spend || 0),
      other_currency_spend: raw.other_currency_spend || [],
      average_order_value: aov,
      currency: raw.currency || this.currency || 'MYR',
      preferred_payment: preferredPayment,
      first_order_date: raw.first_order_date || (raw.created_at || '—'),
      last_order_date: lastOrderDate,
      last_order_relative: lastOrderRelative,
      latest_order_status: latestOrderStatus,
      tags: raw.tags || (lifecycleBadge === 'VIP' ? ['VIP Customer'] : (lifecycleBadge === 'Repeat' ? ['Repeat Buyer'] : ['Customer'])),
      notes: raw.notes || '',
      recent_orders: raw.recent_orders || [],
    };
  }

  // --- Segmentation & Filtering ---

  setSegment(segment: 'ALL' | 'VIP' | 'REPEAT' | 'ONE_TIME' | 'NOT_PAID' | 'AT_RISK') {
    this.activeSegment = segment;
    this.page = 1;
    this.getCustomer();
  }

  setSort(field: 'total_spent' | 'total_orders' | 'last_order_date' | 'customer_name') {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = field === 'customer_name';
    }
    // Sorted by the API across all pages ("Most Orders" ranks by paid orders), so restart from page 1
    this.page = 1;
    this.getCustomer();
  }

  applyLocalFilters() {
    // Rows arrive already sorted by the API
    const result = [...this.allCustomers];
    this.customers = result;
    this.filteredCustomers = result;
  }

  // --- KPI Computed Getters ---

  get totalCustomersCount(): number {
    return this.summary?.total_customers ?? this.allCustomerLength ?? this.allCustomers.length;
  }

  get repeatCustomersCount(): number {
    return this.summary?.repeat_customers ?? 0;
  }

  get payingBuyersCount(): number {
    return this.summary?.period_buyers ?? 0;
  }

  get repeatCustomerRate(): number {
    return this.summary?.repeat_customer_rate ?? 0;
  }

  get averageLifetimeValue(): number {
    return this.summary?.average_lifetime_value ?? 0;
  }

  /** Main currency of the KPI cards: the currency most paid orders in the period were paid in. */
  get kpiCurrency(): string {
    return this.summary?.currency || this.currency;
  }

  /** Average spend in each currency other than the main one; amounts are never converted between currencies. */
  get otherCurrencyAverageSpend(): { currency: string; average_spend: number; buyers: number }[] {
    return (this.summary?.spend_by_currency || []).filter((s) => s.currency !== this.kpiCurrency);
  }

  get totalTrackedRevenue(): number {
    return this.summary?.total_revenue ?? 0;
  }

  get vipCustomersCount(): number {
    return this.summary?.vip_customers ?? 0;
  }

  get newCustomersThisMonthCount(): number {
    return this.summary?.new_customers_this_month ?? this.summary?.new_customers ?? 0;
  }

  get customerGrowthRate(): { percent: string; isPositive: boolean; isNeutral: boolean; isNew?: boolean; hasBadge?: boolean; compareLabel?: string } {
    if (this.summary?.growth_rate) {
      return this.summary.growth_rate;
    }
    return { percent: '0%', isPositive: true, isNeutral: true, hasBadge: false, compareLabel: 'vs previous period' };
  }

  get newCustomersCount(): number {
    return this.summary?.new_customers ?? this.newCustomersThisMonthCount;
  }

  // Segment count helper: strictly from backend summary.segment_counts
  getSegmentCount(segment: string): number {
    return this.summary?.segment_counts?.[segment] ?? 0;
  }

  // Segments as tabs across the top of the customer list.
  readonly segmentTabs: { value: any; label: string; hint?: string }[] = [
    { value: 'ALL', label: 'All Customers' },
    { value: 'VIP', label: 'VIP Customers', hint: 'Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' },
    { value: 'REPEAT', label: 'Repeat' },
    { value: 'ONE_TIME', label: 'One-time buyers' },
    { value: 'NOT_PAID', label: 'Not yet paid' },
    {
      value: 'AT_RISK',
      label: 'At Risk (60–365d)',
      hint: 'No order in the last 60–365 days (Not affected by date filter)',
    },
  ];

  // Phones fold the toolbar filters behind a "Filters" button next to the
  // search; its count says how many of them are narrowing the list.
  toolbarOpen = false;

  get toolbarFilterCount(): number {
    // A custom range clears dateFilter, so this also counts one.
    const dateChanged = this.dateFilter?.value !== 'cm';
    return (dateChanged ? 1 : 0) + (this.filteredCountry ? 1 : 0);
  }

  // A click anywhere on a row opens the customer, unless text was being selected.
  openCustomerFromRow(customer: EnhancedCustomer) {
    if (window.getSelection()?.toString()) return;
    this.openCustomerDetail(customer);
  }

  // --- Customer Detail Mixpanel View & Helpers ---

  openCustomerDetail(customer: EnhancedCustomer, modalContent?: TemplateRef<any>) {
    this.selectedCustomer = customer;
    this.currentView = 'detail';
    this.eventSearchQuery = '';
    this.propertySearchQuery = '';
    this.selectedEventCategory = 'ALL';
    this.hiddenCategories.clear();
    this.isTechnicalDetailsOpen = false;
    this.buildCustomerProperties(customer);
    this.generateCustomerJourney(customer);

    // Scroll smoothly to top
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Call single backend API to retrieve accurate real customer detail & journey from DB
    this.customerDetailSub?.unsubscribe();
    if (this.bearer && this.storeId) {
      this.customerDetailSub = this.svc
        .getCustomerDetail(
          this.bearer,
          this.storeId,
          customer.customer_mobile,
          customer.customer_email,
          customer.customer_id,
          customer.customer_mobile_prefix
        )
        .subscribe(
          (res: any) => {
            if (res?.status === 'success' && res?.data) {
              const detailData = res.data;
              const mergedCustomer = this.enrichCustomer(
                { ...customer, ...detailData },
                0
              );
              this.selectedCustomer = mergedCustomer;
              this.buildCustomerProperties(mergedCustomer);

              if (
                Array.isArray(detailData.journey_events) &&
                detailData.journey_events.length > 0
              ) {
                this.customerEvents = detailData.journey_events;
                this.filterEvents();
              }
            }
          },
          () => {
            // Retain generated view seamlessly if API encounters any network error
          }
        );
    }
  }

  closeCustomerDetail() {
    this.customerDetailSub?.unsubscribe();
    this.currentView = 'list';
    this.selectedCustomer = null;
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  toggleTechnicalDetails() {
    this.isTechnicalDetailsOpen = !this.isTechnicalDetailsOpen;
  }

  getEventPriority(event: CustomerJourneyEvent): number {
    const cat = (event.event_category || '').toLowerCase();
    const name = (event.event_name || event.display_name || '').toLowerCase();
    if (cat === 'delivery' || name.includes('deliver') || name.includes('dispatch') || name.includes('shipping') || name.includes('transit')) {
      return 5;
    }
    if (cat === 'payment' || name.includes('payment') || name.includes('pay_')) {
      return 4;
    }
    if (cat === 'commerce' || cat === 'order' || name.includes('order') || name.includes('checkout')) {
      return 3;
    }
    if (cat === 'session' || cat === 'screen' || name.includes('session') || name.includes('visit')) {
      return 2;
    }
    if (cat === 'marketing' || cat === 'communication' || name.includes('referral') || name.includes('campaign') || name.includes('promo')) {
      return 1;
    }
    return 0;
  }

  buildCustomerProperties(customer: any) {
    const clientMeta = customer.client_metadata || {};

    // 1. Business Profile Properties
    // Deduplicated: summary cards already display tier, status, lifetime orders, lifetime spend, AOV, preferred payment, first order, last active.
    const businessList: CustomerProfileProperty[] = [];

    if (customer.notes) {
      businessList.push({ key: 'Customer Notes', value: customer.notes, icon: 'fi fi-rr-comment-alt' });
    }
    if (customer.preferred_language) {
      businessList.push({ key: 'Preferred Language', value: customer.preferred_language, icon: 'fi fi-rr-globe' });
    }

    // Filter out duplicate tier tags (e.g. 'Repeat Customer', 'Repeat Buyer', 'VIP Customer', 'Customer')
    if (customer.tags?.length) {
      const tierClean = (customer.tier || '').toLowerCase();
      const filteredTags = customer.tags.filter((t: string) => {
        const tLower = t.toLowerCase();
        return !tLower.includes('repeat') && !tLower.includes('vip') && !tLower.includes('buyer') && !tLower.includes('customer') && tLower !== tierClean;
      });
      if (filteredTags.length > 0) {
        businessList.push({ key: 'Tags', value: filteredTags.join(', '), icon: 'fi fi-rr-tags' });
      }
    }

    // 2. Technical Profile Properties (Additional Details)
    const techList: CustomerProfileProperty[] = [
      { key: '$email', value: (customer.customer_email && customer.customer_email !== '-') ? customer.customer_email : 'No email', icon: 'fi fi-rr-envelope', systemProperty: true },
      { key: '$phone', value: customer.customer_mobile ? `+(${customer.customer_mobile_prefix || '60'}) ${customer.customer_mobile}` : '-', icon: 'fi fi-rr-phone-call', systemProperty: true },
      { key: '$city', value: customer.city || '-', icon: 'fi fi-rr-marker', systemProperty: true },
      { key: '$region', value: customer.state || '-', icon: 'fi fi-rr-map', systemProperty: true },
      { key: '$country_code', value: (customer.country_code || 'MY').toUpperCase(), icon: 'fi fi-rr-globe', systemProperty: true },
      { key: '$postal_code', value: customer.postal_code || '-', icon: 'fi fi-rr-envelope-open', systemProperty: true },
      { key: '$address', value: customer.address_line || '-', icon: 'fi fi-rr-home-location', systemProperty: true },
    ];

    if (clientMeta.ip) {
      techList.push({ key: '$ip', value: clientMeta.ip, icon: 'fi fi-rr-network', systemProperty: true });
    }
    if (clientMeta.platform) {
      techList.push({ key: '$device', value: clientMeta.platform, icon: 'fi fi-rr-mobile-button', systemProperty: true });
    }
    if (clientMeta.browser) {
      techList.push({ key: '$browser', value: clientMeta.browser, icon: 'fi fi-rr-browser', systemProperty: true });
    }
    if (clientMeta.referrer) {
      techList.push({ key: '$traffic_source', value: clientMeta.referrer, icon: 'fi fi-rr-share', systemProperty: true });
    }

    this.customerPropertiesList = businessList;
    this.technicalPropertiesList = techList;
    this.filterProperties();
  }

  filterProperties() {
    if (!this.propertySearchQuery) {
      this.filteredPropertiesList = [...this.customerPropertiesList];
      this.filteredTechnicalPropertiesList = [...this.technicalPropertiesList];
      return;
    }
    const q = this.propertySearchQuery.toLowerCase();
    this.filteredPropertiesList = this.customerPropertiesList.filter(
      (p) =>
        p.key.toLowerCase().includes(q) ||
        String(p.value).toLowerCase().includes(q)
    );
    this.filteredTechnicalPropertiesList = this.technicalPropertiesList.filter(
      (p) =>
        p.key.toLowerCase().includes(q) ||
        String(p.value).toLowerCase().includes(q)
    );
  }

  generateCustomerJourney(customer: EnhancedCustomer) {
    const events: CustomerJourneyEvent[] = [];

    // Build real journey events from actual recent orders if available
    if (customer.recent_orders?.length) {
      customer.recent_orders.forEach((ord, idx) => {
        const orderDateStr = moment(ord.order_date).format('D MMM YYYY');
        const timeStr = moment(ord.order_date).format('h:mm A');
        const rawTs = moment(ord.order_date).unix();
        const dateGroup = moment(ord.order_date).isSame(moment(), 'day')
          ? `Today • ${orderDateStr}`
          : (moment(ord.order_date).isSame(moment().subtract(1, 'day'), 'day')
              ? `Yesterday • ${orderDateStr}`
              : orderDateStr);

        // Real Order Placed Event
        events.push({
          id: `ord-${ord.order_id || idx}`,
          raw_timestamp: rawTs,
          event_name: 'Order_Completed',
          display_name: 'Order placed',
          event_category: 'commerce',
          category_label: 'Order',
          category_icon: 'fi fi-rr-box-check',
          category_color: 'badge-cat-commerce',
          timestamp: timeStr,
          date_group: dateGroup,
          status_badge: ord.status,
          action_type: 'copy',
          action_label: 'Copy Order ID',
          action_value: ord.order_id,
          expanded: false,
          properties: {
            'order_id': `#${ord.order_id}`,
            'total_amount': `${ord.currency || customer.currency || this.currency} ${Number(ord.total_amount).toFixed(2)}`,
            'payment_type': this.formatPaymentMethod(ord.payment_type),
            'items_count': ord.items_count || 1,
            'items_preview': ord.items_preview || 'Store Package',
            'delivery_address': customer.address_line || `${customer.city || ''} ${customer.state || ''}`.trim() || '—',
            'fulfillment_status': ord.status || 'Processing',
          },
        });

        // Payment event only when money was received (COD awaiting collection is not paid yet)
        if (ord.is_paid) {
          events.push({
            id: `pay-${ord.order_id || idx}`,
            raw_timestamp: rawTs,
            event_name: 'Payment_Success',
            display_name: 'Payment Success',
            event_category: 'commerce',
            category_label: 'Payment',
            category_icon: 'fi fi-rr-credit-card',
            category_color: 'badge-cat-commerce',
            timestamp: timeStr,
            date_group: dateGroup,
            expanded: false,
            properties: {
              'gateway': this.formatPaymentMethod(ord.payment_type),
              'amount_paid': `${ord.currency || customer.currency || this.currency} ${Number(ord.total_amount).toFixed(2)}`,
              'payment_status': 'Settled',
              'order_ref': `#${ord.order_id}`,
            },
          });
        }
      });
    }

    events.sort((a, b) => {
      const diff = (b.raw_timestamp || 0) - (a.raw_timestamp || 0);
      if (diff !== 0) return diff;
      const pA = this.getEventPriority(a);
      const pB = this.getEventPriority(b);
      if (pB !== pA) return pB - pA;
      return (b.id || '').localeCompare(a.id || '');
    });
    this.customerEvents = events;
    this.filterEvents();
  }

  filterEvents() {
    let result = [...this.customerEvents];

    if (this.hiddenCategories.size > 0) {
      result = result.filter((e) => !this.hiddenCategories.has(e.event_category));
    }

    if (this.selectedEventCategory !== 'ALL') {
      result = result.filter((e) => e.event_category === this.selectedEventCategory);
    }

    if (this.activeTimelineRange === '7d') {
      const cutoff = moment().subtract(7, 'days').unix();
      result = result.filter((e) => !e.raw_timestamp || e.raw_timestamp >= cutoff);
    } else if (this.activeTimelineRange === '30d') {
      const cutoff = moment().subtract(30, 'days').unix();
      result = result.filter((e) => !e.raw_timestamp || e.raw_timestamp >= cutoff);
    }

    if (this.eventSearchQuery) {
      const q = this.eventSearchQuery.toLowerCase();
      result = result.filter((e) => {
        const nameMatch = (e.display_name || e.event_name).toLowerCase().includes(q) || e.event_name.toLowerCase().includes(q);
        const propMatch = Object.entries(e.properties || {}).some(
          ([k, v]) => k.toLowerCase().includes(q) || String(v).toLowerCase().includes(q)
        );
        return nameMatch || propMatch;
      });
    }

    // Sort strictly descending by timestamp with logical priority tiebreaker (Delivery -> Payment -> Order -> Session -> Marketing)
    result.sort((a, b) => {
      const diff = (b.raw_timestamp || 0) - (a.raw_timestamp || 0);
      if (diff !== 0) return diff;
      const pA = this.getEventPriority(a);
      const pB = this.getEventPriority(b);
      if (pB !== pA) return pB - pA;
      return (b.id || '').localeCompare(a.id || '');
    });

    // Group events by date_group preserving order
    const groupsMap = new Map<string, CustomerJourneyEvent[]>();
    result.forEach((evt) => {
      if (!groupsMap.has(evt.date_group)) {
        groupsMap.set(evt.date_group, []);
      }
      groupsMap.get(evt.date_group)!.push(evt);
    });

    this.groupedEvents = Array.from(groupsMap.entries()).map(([date_group, events]) => ({
      date_group,
      events,
    }));
  }

  toggleEventAccordion(event: CustomerJourneyEvent) {
    event.expanded = !event.expanded;
  }

  toggleHideEvents() {
    this.hideEventsDropdownOpen = !this.hideEventsDropdownOpen;
  }

  toggleEventCategory(cat: string) {
    if (this.hiddenCategories.has(cat)) {
      this.hiddenCategories.delete(cat);
    } else {
      this.hiddenCategories.add(cat);
    }
    this.filterEvents();
  }

  isCategoryHidden(cat: string): boolean {
    return this.hiddenCategories.has(cat);
  }

  setTimelineRange(range: '30d' | '7d' | 'all') {
    this.activeTimelineRange = range;
    this.filterEvents();
  }

  getWhatsAppLink(mobile: string, prefix: string): string {
    if (!mobile) return '';
    const cleanPrefix = (prefix || '60').replace(/[^0-9]/g, '');
    let cleanMobile = mobile.replace(/[^0-9]/g, '');
    if (cleanMobile.startsWith('0')) {
      cleanMobile = cleanMobile.substring(1);
    }
    return `https://wa.me/${cleanPrefix}${cleanMobile}`;
  }

  getMailtoLink(email: string): string {
    return `mailto:${email || ''}`;
  }

  copyToClipboard(text: string, label: string = 'Data') {
    if (!text) return;
    this.utils.copyToClipboard(text, label);
  }

  getTierBadgeClass(tier: string): string {
    switch (tier) {
      case 'VIP':
        return 'tier-badge-vip';
      case 'Repeat':
        return 'tier-badge-repeat';
      case 'One-time buyer':
        return 'tier-badge-onetime';
      case 'New':
        return 'tier-badge-new';
      case 'Not yet paid':
        return 'tier-badge-notpaid';
      case 'At Risk':
        return 'tier-badge-risk';
      default:
        return 'tier-badge-default';
    }
  }

  get activePeriodDisplay(): string {
    if (this.dateFilter?.display) {
      return this.dateFilter.display;
    }
    if (this.date?.type?.display) {
      return this.date.type.display;
    }
    return 'This Month';
  }

  getOrderStatusBadgeClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('delivered') || s.includes('completed')) {
      return 'badge-status-completed';
    }
    if (s.includes('delayed') || s.includes('review') || s.includes('confirmation')) {
      return 'badge-status-delayed';
    }
    if (s.includes('awaiting') || s.includes('transit') || s.includes('shipped') || s === 'paid') {
      return 'badge-status-in-transit';
    }
    if (s.includes('processing')) {
      return 'badge-status-processing';
    }
    if (s.includes('cancelled') || s.includes('returned') || s.includes('issue') || s.includes('contact support')) {
      return 'badge-status-cancelled';
    }
    if (s.includes('pending') || s.includes('legacy') || s.includes('subscription')) {
      return 'badge-status-pending-payment';
    }
    return 'badge-status-default';
  }

  // --- Pagination & Navigation ---

  selectPage(pageSize: number) {
    this.selectedPage = pageSize;
    this.page = 1;
    this.getCustomer();
  }

  changePage(pageOfItems: number) {
    this.page = pageOfItems;
    this.getCustomer();
  }

  // --- Date Range Pickers ---

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && (date.after(this.fromDate) || date.equals(this.fromDate))) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  setDueDate() {
    if (!this.fromDate || !this.toDate) {
      return;
    }
    this.DATE_FILTERS.forEach((item) => {
      item.selectable = false;
    });
    this.dateFilter = null;

    const start = moment({
      year: this.fromDate.year,
      month: this.fromDate.month - 1,
      day: this.fromDate.day,
    }).startOf('day');

    const end = moment({
      year: this.toDate.year,
      month: this.toDate.month - 1,
      day: this.toDate.day,
    }).endOf('day');

    if (start.isValid() && end.isValid()) {
      const rangeVal = `${start.format('DD-MMM-YYYY')} to ${end.format('DD-MMM-YYYY')}`;
      this.date.type = {
        value: { start: start, end: end },
        display: rangeVal,
        range: true,
      };
      this.startDate = start.format('YYYY-MM-DD');
      this.endDate = end.format('YYYY-MM-DD');
      this.page = 1;
      this.getCustomer();
    }
  }

  getFlagByCountryCode(country_code: string) {
    country_code = (country_code || '').toLowerCase().substr(-2, 2);
    country_code = country_code === 'cm' ? 'kh' : country_code;
    return country_code;
  }

  getFlagImageURL(country_code: string) {
    country_code = this.getFlagByCountryCode(country_code);
    return `https://cdn.estore5s.com/asset/flags/${country_code}.png`;
  }

  getCountryName(country_code: string) {
    if (country_code === '' || !country_code) {
      return 'All Countries';
    }
    country_code = (country_code || '').toLowerCase();
    const country = this.country?.find(
      (c) => (c.country_code || '').toLowerCase() === country_code
    );
    return country?.country_name ?? country_code.toUpperCase();
  }

  changeCountry(countryCode: string) {
    this.filteredCountry = countryCode;
    this.page = 1;
    this.getCustomer();
  }

  toggleViewMode(mode: 'table' | 'grid') {
    this.viewMode = mode;
  }

  /** Loads the AI Summary price (from the backend config) and whether the button can be used. */
  loadAiCreditState(): void {
    this.dealerService.getAiCreditState('AI_REPORT_SUMMARY_CREDIT').subscribe(({ enabled, tokenCost }) => {
      this.enableAIAssistant = enabled;
      this.aiTokenCost = tokenCost;
    });
  }

  /**
   * Retrieves the authoritative JSON output from v1/customer/list_customer (segment=ALL)
   * to provide the AI Assistant with complete summary KPIs and customer data.
   * The API sorts by most recent order, so a larger sample is fetched to rank top spenders.
   */
  getCustomerReportData(): Observable<any> {
    const t = this.startDate + ',' + this.endDate;
    const cached = this.rawCustomerApiResponse;
    const cachedRows = Array.isArray(cached?.data) ? cached.data.length : 0;

    // Reuse the current response only if it already holds every customer for segment ALL with no search filter
    if (
      cached &&
      this.page === 1 &&
      this.activeSegment === 'ALL' &&
      !this.mobileOrEmail &&
      !this.email &&
      cachedRows >= (cached?.pagination?.total_record ?? Infinity)
    ) {
      return of(cached);
    }

    return this.svc
      .getCustomerList(
        this.bearer,
        this.storeId,
        '',
        '',
        t,
        1,
        this.aiSampleLimit,
        this.filteredCountry || '',
        'ALL'
      )
      .pipe(
        map((res: any) => res?.['data'] || this.rawCustomerApiResponse),
        catchError((err) => {
          console.warn('[customers] Failed to fetch list_customer for AI, falling back to cached state', err);
          return of(this.rawCustomerApiResponse || this.buildFallbackCustomerJson());
        })
      );
  }

  /** Builds fallback JSON schema matching v1/customer/list_customer output if offline or cached */
  private buildFallbackCustomerJson(): any {
    return {
      summary: this.summary || {
        total_customers: this.totalCustomersCount,
        repeat_customers: this.repeatCustomersCount,
        period_buyers: this.payingBuyersCount,
        repeat_customer_rate: this.repeatCustomerRate,
        average_lifetime_value: this.averageLifetimeValue,
        vip_customers: this.vipCustomersCount,
        new_customers: this.newCustomersThisMonthCount,
        growth_rate: this.customerGrowthRate,
        segment_counts: {
          ALL: this.totalCustomersCount,
          VIP: this.vipCustomersCount,
          REPEAT: this.repeatCustomersCount,
          ONE_TIME: this.getSegmentCount('ONE_TIME'),
          NOT_PAID: this.getSegmentCount('NOT_PAID'),
          AT_RISK: this.getSegmentCount('AT_RISK'),
        },
      },
      pagination: {
        page: this.page,
        limit: this.selectedPage,
        total_record: this.allCustomerLength,
      },
      data: (this.allCustomers || []).slice(0, 20),
    };
  }

  /**
   * Condenses the list_customer API JSON into a small, self-describing payload for the AI prompt
   * (GENERATE_CUSTOMER_REPORT_SUMMARY). Field names here are referenced by the prompt template.
   */
  private prepareCustomerApiJson(jsonData: any): string {
    const summary = jsonData?.summary || {};
    const segments = summary.segment_counts || {};
    const growth = summary.growth_rate || {};
    const customers: any[] = Array.isArray(jsonData?.data) ? jsonData.data : [];

    const money = (v: any) => Math.round(Number(v || 0) * 100) / 100;
    const spendOf = (c: any) => Number(c.total_spent_myr ?? c.total_spent ?? 0);
    const paidOrdersOf = (c: any) => Number(c.lifetime_paid_orders ?? 0);
    const sumOf = (key: string) => customers.reduce((acc, c) => acc + Number(c[key] || 0), 0);

    const describe = (c: any) => ({
      name: c.customer_name || 'Customer',
      tier: c.lifecycle_badge || c.tier || '',
      all_time_spent: money(spendOf(c)),
      all_time_paid_orders: paidOrdersOf(c),
      last_order: (c.last_order_date || '').slice(0, 10) || undefined,
      location: [c.city, c.state].filter(Boolean).join(', ') || undefined,
    });

    const topBy = (score: (c: any) => number, minScore: number) =>
      [...customers]
        .filter((c) => score(c) >= minScore)
        .sort((a, b) => score(b) - score(a))
        .slice(0, 5)
        .map(describe);

    const countBy = (keyOf: (c: any) => string) => {
      const counts: Record<string, number> = {};
      customers.forEach((c) => {
        const key = keyOf(c);
        if (key) counts[key] = (counts[key] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, count]) => ({ name, customers: count }));
    };

    const payload = {
      report_period: this.activePeriodDisplay || `${this.startDate} to ${this.endDate}`,
      currency: summary.currency || this.currency || 'MYR',
      summary: {
        total_customers: summary.total_customers ?? 0,
        period_buyers: summary.period_buyers ?? 0,
        repeat_customer_rate: summary.repeat_customer_rate ?? 0,
        average_spend_per_buyer: money(summary.average_lifetime_value),
        total_revenue: money(summary.total_revenue),
        // Per-currency breakdown; amounts in different currencies must not be added together
        spend_by_currency: summary.spend_by_currency ?? [],
        new_customers: summary.new_customers ?? 0,
        vip_customers: summary.vip_customers ?? 0,
        store_wide_at_risk_customers: summary.at_risk_customers ?? 0,
        growth_rate: growth.hasBadge === false ? '' : growth.percent || '',
        growth_compare_label: growth.compareLabel || '',
        segment_counts: {
          VIP: segments.VIP ?? 0,
          REPEAT: segments.REPEAT ?? 0,
          ONE_TIME: segments.ONE_TIME ?? 0,
          NOT_PAID: segments.NOT_PAID ?? 0,
        },
      },
      customers_analyzed: customers.length,
      top_spenders: topBy(spendOf, 0.01),
      most_frequent_buyers: topBy(paidOrdersOf, 2),
      payment_methods: countBy((c) => (c.preferred_payment ? this.formatPaymentMethod(c.preferred_payment) : '')),
      top_locations: countBy((c) => c.state || c.city || ''),
      all_time_unpaid_orders: {
        customers: customers.filter((c) => Number(c.unpaid_orders) > 0).length,
        orders: sumOf('unpaid_orders'),
      },
      all_time_awaiting_collection_orders: sumOf('pending_orders'),
      inactive_customers_analyzed: customers.filter((c) => c.status === 'Inactive').length,
    };

    return JSON.stringify(payload);
  }

  /** Sends the Mixpanel AI Generate event; ai_purpose comes from the clicked button's data-ai-purpose. */
  private trackAiGenerate(event: 'AI Generate Success' | 'AI Generate Fail', errorResponse?: string): void {
    this.svc.trackAiGenerateEvent(event, {
      aiPurpose: this.aiPurpose,
      storeId: this.storeId,
      token: this.aiTokenCost || 0,
      errorResponse,
    });
  }

  /** Triggers the AI Assistant to analyze and summarize customer data using the list_customer API JSON output. */
  generateAiSummary(event?: Event): void {
    if (this.isGeneratingAiSummary) return;
    this.aiPurpose = (event?.currentTarget as HTMLElement)?.dataset?.aiPurpose || this.aiPurpose;

    this.isGeneratingAiSummary = true;
    this.aiSummaryError = null;
    this.showAiSummaryCard = true;
    this.isCopied = false;

    this.getCustomerReportData().subscribe(
      (jsonData: any) => {
        // Summary is always returned (even all zeros), so check for actual customers to avoid spending credits on an empty report
        const hasCustomers = (jsonData?.summary?.total_customers ?? 0) > 0 || (jsonData?.data?.length ?? 0) > 0;
        if (!hasCustomers) {
          this.isGeneratingAiSummary = false;
          this.aiSummaryError = 'No customer data available for the selected period to summarize.';
          this.trackAiGenerate('AI Generate Fail', this.aiSummaryError);
          return;
        }

        const reportData = this.prepareCustomerApiJson(jsonData);

        const params: any = {
          type: 'GENERATE_CUSTOMER_REPORT_SUMMARY',
          report_data: reportData,
          desc: 'AI Customer Insights & Cohort Summary Generation',
        };

        this.svc.getAIGeneratedContent(params).subscribe(
          (res: any) => {
            this.isGeneratingAiSummary = false;
            let content = res?.data;
            if (typeof content === 'object') {
              content = JSON.stringify(content, null, 2);
            }
            this.aiSummaryContent = content || 'No summary generated.';
            this.parseAiSummary();
            this.loadAiCreditState();
            this.trackAiGenerate('AI Generate Success');
          },
          (err: any) => {
            this.isGeneratingAiSummary = false;
            console.error('[customers] AI generation failed', err);
            const serverMsg =
              (typeof err?.error?.message === 'string' ? err.error.message : null) ||
              (typeof err?.error?.data?.error === 'string' ? err.error.data.error : null) ||
              (typeof err?.error?.data?.message === 'string' ? err.error.data.message : null) ||
              (typeof err?.error?.error === 'string' ? err.error.error : null) ||
              (typeof err?.error?.message?.error === 'string' ? err.error.message.error : null) ||
              (typeof err?.error?.data?.message?.error === 'string' ? err.error.data.message.error : null) ||
              (err?.status === 414 ? 'Request payload was too large. Please retry.' : null);
            const errorMsg =
              serverMsg ||
              this.utils?.getErrorMessageWithRefId?.(
                err,
                'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.'
              ) ||
              'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.';
            this.aiSummaryError = errorMsg;
            this.trackAiGenerate('AI Generate Fail', errorMsg);
          }
        );
      },
      (err: any) => {
        this.isGeneratingAiSummary = false;
        console.error('[customers] Failed to retrieve customer API JSON', err);
        this.aiSummaryError = 'Unable to fetch the latest customer data for AI analysis. Please try again.';
        this.trackAiGenerate('AI Generate Fail', this.aiSummaryError);
      }
    );
  }


  /** Parses raw AI text into clean, structured sections for modern card rendering. */
  parseAiSummary(): void {
    this.parsedSections = parseAiInsightSections(this.aiSummaryContent, CUSTOMER_AI_SECTIONS, { splitNotes: true });
  }

  /** Copies generated AI summary text to clipboard. */
  copyAiSummary(): void {
    if (!this.aiSummaryContent) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.aiSummaryContent).then(() => {
        this.isCopied = true;
        this.utils?.notify?.showSuccess?.(
          'AI Summary has been copied to clipboard',
          'Copied!'
        );
        clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.isCopied = false;
        }, 3000);
      });
    } else {
      this.utils?.copyToClipboard?.(this.aiSummaryContent, 'AI Summary');
    }
  }

  /** Closes/hides the AI summary card. */
  dismissAiSummary(): void {
    this.showAiSummaryCard = false;
  }
}
